<script setup lang="ts">
import { getFeedback, setContext, setUser } from '@sentry/vue';
import { computed, onMounted, ref, watch, type ComponentPublicInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useAuthStore } from '@/stores/AuthStore.ts';

import { PlatformRights } from '@/clients/v1';
import { hasPlatformRights, isOrgAdmin, useAuth } from '@/composables/Auth.ts';
import { viteSentryEnabled } from '@/config/env.ts';
import { errorMessage } from '@/composables/Notify.ts';
import { captureError } from '@/composables/Sentry.ts';
import { useLanguages } from '@/composables/useLanguages.ts';
import { profileModalIds } from '@/utils/DataTestIds.ts';
import { userInitials } from '@/utils/formatting.ts';

import ABadge from '@/components/atoms/Badges/ABadge.vue';
import AIconButton from '@/components/atoms/AIconButton/AIconButton.vue';
import OTabPanels from '@/components/organisms/OTabPanels.vue';

const emit = defineEmits(['hide']);

const props = defineProps({
  showDialog: {
    type: Boolean,
    required: true,
  },
});

const { t, locale } = useI18n();
const { logout } = useAuth();
const router = useRouter();
const authStore = useAuthStore();
const { selectedLanguage, availableLanguages, initializeLanguage } = useLanguages();
const iconFeedbackRef = ref<ComponentPublicInstance | null>(null);

const activeTab = ref('settings');
const showing = ref(false);
const updatingLanguage = ref(false);

function changeTab(value: string) {
  activeTab.value = value;
  localStorage.setItem('scope-active-tab', value);
}

const settingsItems = computed(() => {
  return [
    {
      id: 'admin',
      iconName: 'o_person',
      text: t('Admin'),
      to: '/admin',
      visible: hasPlatformRights(PlatformRights.PlatformOrganizationsRead),
    },

    {
      id: 'settings',
      iconName: 'o_settings',
      text: t('Settings'),
      to: '/settings',
      visible: true,
    },

    {
      id: 'language',
      iconName: 'o_language',
      visible: true,
      text: selectedLanguage.value?.label || t('Language'),
      children: availableLanguages.value,
    },
  ];
});

function toPage(to: string) {
  router.push({ path: to });
}

async function selectLanguage(code: string) {
  const language = availableLanguages.value.find((lang) => lang.value === code);

  if (!language) return;

  try {
    updatingLanguage.value = true;

    await authStore.updateMe({
      updateUser: {
        language: code,
      },
    });

    // Update localStorage immediately before reload
    const localeKey = `${language.value}-${language.country.toUpperCase()}`;
    localStorage.setItem('selectedLanguage', localeKey);

    window.location.reload();
  } catch (error) {
    errorMessage(error);

    captureError(error, {
      message: 'Component: DProfile, Method: selectLanguage',
    });
  } finally {
    updatingLanguage.value = false;
  }
}

function handleItemClick(item: {
  id: string;
  iconName: string;
  text: string;
  to?: string;
  children?: Array<{ label: string; value: string; country: string }>;
}) {
  if (item.to) {
    toPage(item.to);
    showing.value = false;
  }
}

watch(
  () => props.showDialog,
  (newValue) => {
    showing.value = newValue;
  },
  { immediate: true },
);

watch(
  () => locale.value,
  () => {
    const matchedLanguage = availableLanguages.value.find(
      (lang) => lang.value === selectedLanguage.value?.value,
    );

    if (!matchedLanguage) return;

    selectedLanguage.value = matchedLanguage;
  },
  { immediate: true },
);

watch(
  () => iconFeedbackRef.value,
  (newValue) => {
    if (newValue) {
      const el = newValue.$el;

      getFeedback()?.attachTo(el, {
        showName: false,
        showEmail: false,
        colorScheme: 'light',
        showBranding: false,
        triggerLabel: '',
        buttonLabel: 'Provide Feedback',
        submitButtonLabel: 'Send Your Feedback',
        formTitle: 'Provide Feedback',
        successMessageText: 'Thanks for your feedback!',
        messagePlaceholder:
          'Please share any positive experiences or let us know if you encountered any issues or bugs.',
        onFormOpen: () => {
          if (authStore.user) {
            setUser({
              id: authStore.user.id,
            });
            setContext('organization', {
              id: authStore.user.organization.id,
              name: authStore.user.organization.name,
            });
          }
        },
      });
    }
  },
);

onMounted(() => {
  const languageCode = locale.value.toLowerCase().split('-')[0];
  initializeLanguage(languageCode);
});
</script>

<template>
  <div v-if="authStore.user" :class="$q.screen.lt.sm ? 'mobile-profile' : ''">
    <q-menu
      v-model="showing"
      :context-menu="false"
      anchor="bottom start"
      self="top end"
      fit
      :offset="[20, 20]"
      transition-hide="jump-up"
      class="profile-menu"
      @hide="emit('hide')"
    >
      <q-card class="q-pa-none" style="min-width: 280px">
        <q-card-section class="q-pa-lg">
          <div class="row items-start no-wrap">
            <q-avatar size="40px" class="a-avatar-neutral">
              <span>{{ userInitials(authStore.user) }}</span>
            </q-avatar>

            <div class="q-ml-md">
              <p class="ellipsis text-body2">
                {{ authStore.user.firstname + ' ' + authStore.user.lastname }}
              </p>
              <p class="text-caption text-muted ellipsis">
                {{ authStore.user.email }}
              </p>
              <ABadge
                v-if="isOrgAdmin()"
                tone="neutral"
                class="q-mt-xs"
                :badge-label="t('Admin')"
              />
            </div>

            <AIconButton
              class="q-ml-auto"
              icon="logout"
              :data-test-id="profileModalIds.profileModalLogoutIcon"
              @click="logout"
            />
          </div>
        </q-card-section>

        <q-separator />

        <OTabPanels
          :active-tab="activeTab"
          :tab-size="{ md: 12, lg: 12 }"
          default-tab="settings"
          class="tabs__container"
          @change-tab="changeTab"
        >
          <template #panels>
            <q-tab-panel name="settings" class="q-pa-none">
              <q-list dense>
                <q-item
                  v-for="item in settingsItems"
                  v-show="item.visible"
                  :key="item.id"
                  v-ripple
                  clickable
                  @click="handleItemClick(item)"
                >
                  <q-item-section avatar class="col-2">
                    <q-icon :name="item.iconName" size="18px" class="text-muted" />
                  </q-item-section>

                  <q-item-section>
                    <span class="text-body2 text-body">{{ item.text }} </span>
                  </q-item-section>

                  <q-item-section v-if="item.children" side class="col-2">
                    <q-icon name="o_arrow_forward_ios" size="16px" class="text-muted" />
                  </q-item-section>

                  <q-menu
                    v-if="item.children"
                    anchor="top end"
                    self="top start"
                    transition-show="jump-down"
                    transition-hide="jump-up"
                    class="language__menu"
                  >
                    <q-list style="min-width: 100px">
                      <q-item
                        v-for="child in availableLanguages"
                        :key="child.value"
                        clickable
                        :active="locale.toLowerCase() === child.value + '-' + child.country"
                        class="items-center"
                        :disable="updatingLanguage"
                        @click="selectLanguage(child.value)"
                      >
                        <q-icon
                          v-if="locale.toLowerCase() === child.value + '-' + child.country"
                          size="12px"
                          name="o_check"
                          class="q-mr-sm text-muted"
                        />
                        <span class="text-body2 text-body">{{ child.label }}</span>
                      </q-item>
                    </q-list>
                  </q-menu>
                </q-item>
                <q-item v-if="viteSentryEnabled" ref="iconFeedbackRef" v-ripple clickable>
                  <q-item-section avatar class="col-2">
                    <q-icon name="o_feedback" size="18px" class="text-muted" />
                  </q-item-section>

                  <q-item-section>
                    <span class="text-body2 text-body">{{ t('Provide Feedback') }} </span>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-tab-panel>
          </template>
        </OTabPanels>
      </q-card>
    </q-menu>
  </div>
</template>

<style lang="scss" scoped>
.tabs__container {
  margin-top: -20px;
}

.q-item {
  padding: 10px 24px !important;
}

.q-item:not(:last-child) {
  border-bottom: 1px solid $primary-100;
}

.language__menu {
  .q-item--active {
    background-color: $primary-50 !important;
    color: var(--color-text-body);
  }
}

.mobile-profile {
  position: fixed;
  bottom: 50px;
  z-index: $z-index-very-high;
}
</style>
